import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GSErrors } from '../../models';
import { GetJWTResponse } from '../../models/gigya/accounts';
import { NavigationStart, Router } from '@angular/router';
// noinspection TypeScriptPreferShortImport
import { GigyaService } from '../gigya/gigya.service';
// noinspection TypeScriptPreferShortImport
import { JwtUtilsService } from '../jwt-utils/jwt-utils.service';
// noinspection TypeScriptPreferShortImport
import { TimeUtilsService } from '../time-utils/time-utils.service';
import { AuthToken, IdToken } from '../../models/jwt';
import { Dc, DcEnvUtilsService } from '../dc-env-utils/dc-env-utils.service';
import { CookieService } from 'ngx-cookie-service';
import { AUTH_TOKEN_URL, GLOBAL, Global, INVALIDATE_AUTH_TOKEN_URL } from '../../injection-tokens';
import { BehaviorSubject, fromEvent, interval, merge, Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { filter, switchMap } from 'rxjs/operators';
import { fromMillisecondsToMinutes } from '../../utils';
import { DEFAULT_THEME, SELECTED_THEME_KEY, UniThemingService } from '../theming/theming.service';

type PromiseCallback = {
  resolve: (result?: any) => void,
  reject: (reason?: any) => void
};

export type RolesGlobalApi = {
  roles: {
    get: () => Promise<string[]>,
    has: (roles: string | string[]) => Promise<boolean>,
    add: (roles: string | string[]) => Promise<boolean>,
    remove: (roles: string | string[]) => Promise<boolean>,
    clear: () => Promise<boolean>
  }
};

const SKEW = 60;
const ID_TOKEN_REFRESH_SECONDS = 120;
export const initTimeMilliseconds = Date.now();
export const MAX_ALLOWED_INACTIVITY_DURATION_MINUTES = 30;
export const lastSelectedTenantId = 'last-selected-tenant-id';
export const lastSelectedWorkspaceId = 'last-selected-workspace-id';
export const lastSelectedBusinessUnitId = 'last-selected-business-unit-id';
export const lastUserActivityStamp = 'last-user-activity-stamp';
export const EXPLORATION_SAMPLE_COLUMNS = 'exploration_sample';
export const RELATIONSHIP_SAMPLE_COLUMNS = 'relationship_sample';


let hmrCache: {
  idToken: IdToken,
  authToken: AuthToken;
};

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  isSAMLSession = this._global.location.hostname.includes('my.');

  get idToken(): IdToken {
    return this._idToken;
  }

  set idToken(value: IdToken) {
    this._idToken = value;
    if (this._global.isHMR) {
      hmrCache.idToken = value;
    }
  }

  get authToken(): AuthToken {
    return this._authToken;
  }

  set authToken(value: AuthToken) {
    if (this._global.isHMR) {
      hmrCache.authToken = value;
    }
  }

  private _loggingOut$ = new BehaviorSubject<boolean>(false);
  loggingOut$ = this._loggingOut$.asObservable();

  private set lastUserActivityTime(stamp: number) {
    this._global.localStorage?.setItem(lastUserActivityStamp, stamp.toString());
  }

  private get lastUserActivityTime(): number {
    return parseInt(this._global.localStorage.getItem(lastUserActivityStamp), 10);
  }

  private set loggingOut(loggingOut: boolean) {
    this._loggingOut$.next(loggingOut);
  }
  private get loggingOut(): boolean {
    return this._loggingOut$.value
  }

  private _idToken: IdToken;
  private _authToken: AuthToken;
  private subscriptions: Subscription = new Subscription();
  private refreshingSession = false;
  private refreshQueue: Array<PromiseCallback> = [];
  private readonly isEnvIL: boolean;
  private readonly dc: Dc;

  constructor(
    private router: Router,
    private http: HttpClient,
    private gigya: GigyaService,
    private jwtUtils: JwtUtilsService,
    private timeUtils: TimeUtilsService,
    private envUtils: DcEnvUtilsService,
    private cookieService: CookieService,
    private _themingService: UniThemingService,
    @Inject(DOCUMENT) _document: Document,
    @Inject(GLOBAL) private _global: Global & RolesGlobalApi,
    @Inject(AUTH_TOKEN_URL) private authTokenUrl: string,
    @Inject(INVALIDATE_AUTH_TOKEN_URL) protected invalidateAuthTokenUrl: string
  ) {
    this._themingService.setInitialTheme();
    this.lastUserActivityTime = Date.now();
    this.dc = this.envUtils.getDc();
    this.isEnvIL = this.dc === 'il1' || this.dc === 'il3';

    const windowFocus$ = fromEvent(_global, 'focus');
    const documentClick$ = fromEvent(_document, 'click');
    const documentKeyup$ = fromEvent(_document, 'keyup');
    const documentContextMenu$ = fromEvent(_document, 'contextmenu');
    const navigationStart$ =
      this.router.events.pipe(
        filter(navigationEvent =>
          navigationEvent instanceof NavigationStart)
      );

    const userInteractionEvent$ = merge(
      windowFocus$,
      documentClick$,
      documentKeyup$,
      documentContextMenu$,
      navigationStart$
    );

    this.subscriptions.add(
      userInteractionEvent$
        .subscribe(() =>
          this.checkForInactivity())
    );

    if (this._global.isHMR) {
      if (hmrCache) {
        this._idToken = hmrCache.idToken;
        this._authToken = hmrCache.authToken;
      } else {
        hmrCache = {
          idToken: undefined,
          authToken: undefined
        };
      }
    }
  }

  private validateIdToken(): boolean {
    return this.idToken &&
      this.timeUtils.isValid(this.idToken.iat, ID_TOKEN_REFRESH_SECONDS - SKEW);
  }

  private validateAuthToken(): boolean {
    return this.cookieService.get('HasSession') !== '' &&
      this.cookieService.get('SessionDC') === this.dc;
  }

  public async refreshIdToken(): Promise<void> {
    const res: GetJWTResponse = await this.gigya.getJWT();
    if (res.errorCode !== GSErrors.OK || !res.id_token) {
      const errorMessage =
        (res.errorCode === GSErrors.UNAUTHORIZED_USER ||
          res.errorCode === GSErrors.PERMISSION_DENIED)
          ? 'unauthorized user'
          : (res.errorMessage || 'failed to get JWT');
      console.error(errorMessage);
      return this.logout();
    }
    const parsedJwt = this.jwtUtils.parseJwt(res.id_token);
    this.idToken = {token: res.id_token, exp: parsedJwt.exp, iat: parsedJwt.iat};
  }

  async refreshAuthToken(): Promise<void> {
    if (!this.validateIdToken()) {
      await this.refreshIdToken();
    }
    const authTokenRes = await this.fetchAuthToken();
    this.cookieService.set('SessionDC', this.dc, {domain: 'gigya.com'});
    this.authToken = {exp: authTokenRes.Expiration};
    this._authToken = { exp: authTokenRes.Expiration };
  }


  public fetchAuthToken(): Promise<GetJWTResponse> {

    const formData = new FormData();
    formData.append('format', 'json');
    formData.append('local', this.isEnvIL.toString());
    formData.append('id_token', this.idToken?.token || '');

    return this.http.post<GetJWTResponse>(this.authTokenUrl, formData, { withCredentials: true })
      .toPromise()
      .catch(e => {
        console.error('fetchAuthToken failed', e);
      }) as Promise<GetJWTResponse>;
  }

  async refreshSession(): Promise<void> {

    return new Promise<void>(async (resolve, reject) => {

      this.refreshQueue.push({resolve, reject});

      if (this.refreshingSession) {
        return;
      }

      let hasValidSession = true;
      this.refreshingSession = true;

      try {

        // first, make sure we have a valid id-token.
        // it's required for getting an auth-token
        if (!this.validateIdToken()) {
          await this.refreshIdToken();
          await this.refreshAuthToken();
        }

        // we know for sure that we have a valid it-token.
        // we can now make sure we have a valid auth-token
        if (!this.validateAuthToken()) {
          await this.refreshAuthToken();
        }

      } catch (e) {
        hasValidSession = false;
        console.error(e);

      } finally {

        this.refreshQueue.forEach(x =>
          hasValidSession ?
            x.resolve() :
            x.reject({
              status: 'FAIL',
              errorMessage: 'Unauthorized user',
              statusMessage: 'Unauthorized user',
              errorCode: 403005
            }));

        this.refreshQueue = [];
        this.refreshingSession = false;
      }
    });
  }

  public invalidateAuthToken(): Promise<GetJWTResponse> {
    this.idToken = null;
    this.authToken = null;
    const formData = new FormData();
    formData.append('format', 'json');

    return this.http.post<GetJWTResponse>(this.invalidateAuthTokenUrl, formData, { withCredentials: true }).toPromise()
      .catch(e => console.error('invalidateAuthToken failed', e)) as Promise<GetJWTResponse>;
  }

  public async checkForInactivity(): Promise<void> {
    const isUserSessionEnd = this.isUserSessionTimedOut();
    if (isUserSessionEnd ) {
      let rolesApi = this._global?.roles;
      if (!rolesApi) {
        // roles api is not ready yet
        try {
          await new Promise<void>((resolve, reject) => {
            let retryCounter = 0;
            const INTERVAL_DELAY_MS = 100;
            const MAX_RETRIES = 50;
            const intervalId = setInterval(() => {
              rolesApi = this._global?.roles;
              if (rolesApi) {
                clearInterval(intervalId);
                resolve();
              } else {
                if (retryCounter++ === MAX_RETRIES) {
                  clearInterval(intervalId);
                  reject(`roles api is not ready after ${INTERVAL_DELAY_MS * MAX_RETRIES} ms`);
                }
              }
            }, INTERVAL_DELAY_MS);
          });
        } catch (e) {
          // console.warn(e);
        }
      }

      const roles = (await rolesApi?.get()) ?? [];
      if (!roles.includes('developer') &&
        !roles.includes('e2e-test')) {
        await this.logout();
      }
    }

    this.lastUserActivityTime = Date.now();
  }

  public isUserSessionTimedOut(): boolean {
    const millisecondsSinceLastUserActivity = Date.now() - this.lastUserActivityTime;
    const minutesSinceLastUserActivity = fromMillisecondsToMinutes(millisecondsSinceLastUserActivity);
    return minutesSinceLastUserActivity > MAX_ALLOWED_INACTIVITY_DURATION_MINUTES;
  }

  public isSessionNew(): boolean {
    const millisecondsSinceInitTime = Date.now() - initTimeMilliseconds;
    const minutesSinceInitTime = fromMillisecondsToMinutes(millisecondsSinceInitTime);
    return minutesSinceInitTime < MAX_ALLOWED_INACTIVITY_DURATION_MINUTES;
  }

  public clearLocalStorage(): void {
    const keysToPreserve = {
      [lastSelectedTenantId]: this._global.localStorage.getItem(lastSelectedTenantId),
      [lastSelectedWorkspaceId]: this._global.localStorage.getItem(lastSelectedWorkspaceId),
      [lastSelectedBusinessUnitId]: this._global.localStorage.getItem(lastSelectedBusinessUnitId),
      [SELECTED_THEME_KEY]: this._global.localStorage.getItem(SELECTED_THEME_KEY) ?? DEFAULT_THEME,
      [EXPLORATION_SAMPLE_COLUMNS]: this._global.localStorage.getItem(EXPLORATION_SAMPLE_COLUMNS),
    };

    this._global.localStorage.clear();

    Object.entries(keysToPreserve).forEach(([key, value]) => {
      if (value !== null) {
        this._global.localStorage.setItem(key, value);
      }
    });
  }

  private async logout(): Promise<void> {
    if (!this.router.url.startsWith('/auth/') && !this.loggingOut) {
      try {
        this.loggingOut = true;
        const redirectUrl = this._global.encodeURIComponent(this.router.routerState.snapshot.url);
        await this.router.navigateByUrl(`/auth/logout?redirectUrl=${redirectUrl}&inactivity=true`);
        await this.gigya.logout();
        await this.invalidateAuthToken();
        this.cookieService.deleteAll('/');
        this.clearLocalStorage();
      } catch (e) {
        console.error('failed to logout', e);
        this.loggingOut = false;
      }
    }
  }
}
